/**
 * Voorgestelde agent-acties bij een AI-draft (mail-agent, afnemer 2 van de
 * verb-registry) — de client-kant: types (spiegel van
 * api/_lib/agent-verbs/mail-acties.ts en preview.ts, gestript van
 * server-only zaken), de kaart-labels en de afwijsredenen.
 *
 * Trede 2 (04-09): de kaart toont de preview uit de handler en heeft
 * Akkoord/Afwijzen; de klik gaat naar POST /api/inbox/actie met alleen
 * message_id + actie_index + besluit (+ reden/keuze). De server leest het
 * voorstel zelf; de client stuurt nooit params mee.
 *
 * Plannen: plans/2026-09-02-mail-agent-afnemer-2-verb-registry.md (stap 9),
 *          plans/2026-09-04-mail-agent-trede-2-klik-preview-verbdekking.md (stap 7)
 */

import type { KlachtRegistratieBewijs } from './klacht-afhandeling';
import type { OfferteBewijs } from './offerte-resultaat';
import type { OpdrachtBewijs, BevestigingBewijs } from './vervolgopdracht-resultaat';

export interface VerbPreview {
  afdoeningBron?: string;
  partnerBron?: string;
  partnerExecutionSnapshot?: Record<string, unknown>;
  partnerPresentationSnapshot?: { samenvatting: string; details: string[] };
  partnerSnapshotRevision?: number;
  partnerHerstelVan?: { besluitId: string | null; beslotenOp: string; status: 'pre_write_mislukt' };
  gewijzigdeVelden?: string[];
  orderwijzigingBron?: string;
  offertewijzigingBron?: string;
  naleveringBron?: string;
  factuurBron?: string;
  offerteBron?: string;
  vervolgopdrachtBron?: string;
  klachtChauffeur?: import('./klacht-chauffeur').KlachtChauffeurKeuze;
  samenvatting: string;
  details?: string[];
  blokkade?: string;
  /** Wachtende kaart: sleutels waarop een latere mail (ander gesprek) de kaart aanvult. */
  wacht_sleutels?: string[];
  opties?: Array<{ nummer: number; dag: string; datum?: string }>;
}

export type AfwijsRedenCode = 'verkeerde_order' | 'verkeerde_gegevens' | 'anders_bedoeld' | 'al_gedaan' | 'anders';

export const AFWIJS_REDENEN: Array<{ code: AfwijsRedenCode; label: string }> = [
  { code: 'verkeerde_order', label: 'Verkeerde order of offerte' },
  { code: 'verkeerde_gegevens', label: 'Gegevens kloppen niet (adres, maten, datum...)' },
  { code: 'anders_bedoeld', label: 'Klant bedoelde iets anders' },
  { code: 'al_gedaan', label: 'Al gedaan' },
  { code: 'anders', label: 'Anders (toelichten)' },
];

export interface ActieUitkomst {
  [key: string]: unknown;
  nalevering?: import('./productnalevering').ProductNaleveringBewijs;
  afdoening?: import('./mail-afdoening').MailAfdoening;
  partnerconcept?: import('./partnerconcept').PartnerConcept;
  documenten?: import('./factuurconcept').FactuurDocument[];
  offerteDocumenten?: import('./offerteconcept').OfferteDocument[];
  besluit: 'akkoord' | 'afgewezen';
  besluit_id?: string;
  akkoord_soort?: 'direct' | 'met_keuze';
  door: string;
  at: string;
  ok?: boolean;
  uitleg?: string | null;
  gewijzigde_velden?: string[];
  partner_herstel?: {
    status: 'pre_write_mislukt' | 'onzeker';
    controles: Record<'agent_actie_runs' | 'mail_partner_runs' | 'external_lead' | 'partnerregistratie', 'afwezig' | 'gevonden' | 'onleesbaar'>;
    uitleg: string;
  };
  klacht?: KlachtRegistratieBewijs;
  offerte?: OfferteBewijs;
  opdracht?: OpdrachtBewijs;
  bevestiging?: BevestigingBewijs;
  reden?: { code: AfwijsRedenCode; tekst?: string };
}

export interface VoorgesteldeActie {
  vraag?: import('./mail-vraagplan').VraagActieBinding;
  verb: string;
  params: Record<string, unknown>;
  /** Citaat of parafrase uit de mail waarop het voorstel rust. */
  reden: string;
  preview?: VerbPreview;
  uitkomst?: ActieUitkomst;
  uitvoering?: { gestart_op: string };
}

/** Modus per mail-verb (uit /api/inbox/thread); bepaalt de badge en of er knoppen staan. */
export type MailVerbModi = Record<string, 'uit' | 'schaduw' | 'live' | 'voorbereiden'>;

const MAX_LABEL = 60;

function kort(s: unknown, max = MAX_LABEL): string {
  const t = typeof s === 'string' ? s.trim() : '';
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

function s(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

const ITEM_ACTIE_LABEL: Record<string, string> = {
  kleed_toevoegen: 'Kleed toevoegen',
  kleed_vervallen: 'Kleed laten vervallen',
  behandeling_toevoegen: 'Behandeling toevoegen',
};

/** Leesbaar kaart-label per verb; onbekend verb valt terug op de verb-naam. */
export function actieLabel(actie: VoorgesteldeActie): string {
  const p = actie.params ?? {};
  const ref = s(p.order_referentie) ? ` ${s(p.order_referentie)}` : '';
  switch (actie.verb) {
    case 'verwerk_inkooporder': return `Inkooporder verwerken${s(p.aantal_kleden) ? ` · ${s(p.aantal_kleden)} kleden` : ''}`;
    case 'sluit_mail_zonder_antwoord': return 'Afhandelen zonder antwoord';
    case 'bereid_factuurconcept': return `Facturen als conceptbijlage voorbereiden${ref}`;
    case 'bereid_offerteconcept': return `Offerte ${s(p.offerte_referentie) || s(p.order_referentie)} als verzekeringsbijlage voorbereiden`;
    case 'vraag_klachtbeoordeling': return `Wasserijbeoordeling ${s(actie.params.item_referentie)}`;
    case 'verwerk_klachtkeuze': return 'Klantkeuze bij klachtvoorstel controleren';
    case 'corrigeer_ritkosten':
      return `Ritkosten${ref}: € ${Number(p.bedrag_incl_btw).toFixed(2)} corrigeren`;
    case 'registreer_klacht':
      return `Klacht${ref}: ${p.afhandeling === 'antwoord_volstaat' ? 'afhandelen met dit antwoord' : 'registreren voor opvolging'}`;
    case 'annuleer_reparatie':
      return `Reparatie ${p.reparatie_referentie || ''} annuleren (order ${p.order_referentie || ''})`;
    case 'plaats_opmerking':
      return `Opmerking chauffeur${ref}: ${kort(p.opmerking)}`;
    case 'sluit_lead': {
      const lref = s(p.lead_referentie) ? ` ${s(p.lead_referentie)}` : '';
      const reden = s(p.reden) ? `, ${s(p.reden)}` : '';
      return `Offerte${lref} sluiten: ${kort(p.status, 40)}${reden}`;
    }
    case 'wijzig_adres': {
      const reikwijdte = s(p.reikwijdte) || (p.is_verhuizing === true ? 'verhuizing' : s(p.kant));
      const kant = ({ ophalen: 'ophaaladres', afleveren: 'afleveradres', beide: 'ophaal- en afleveradres', account: 'klantadres', verhuizing: 'verhuizing' } as Record<string, string>)[reikwijdte] || 'adres';
      const toev = s(p.toevoeging) ? ` ${s(p.toevoeging)}` : '';
      return `${kant[0]!.toUpperCase()}${kant.slice(1)}${ref} → ${s(p.straat_huisnummer)}${toev}, ${s(p.postcode_nieuw)} ${s(p.plaats)}`;
    }
    case 'wijzig_order_items': {
      if (p.alleen_prijs === true) return 'Prijsvoorstel voor orderwijziging (order blijft ongewijzigd)';
      const actieTekst = ITEM_ACTIE_LABEL[s(p.actie)] ?? s(p.actie);
      const maat = typeof p.lengte_cm === 'number' && typeof p.breedte_cm === 'number' ? ` ${Math.round(p.lengte_cm)} x ${Math.round(p.breedte_cm)} cm` : '';
      const beh = s(p.behandeling) ? ` (${s(p.behandeling)})` : '';
      const item = typeof p.item_nummer === 'number' ? ` kleed ${p.item_nummer}` : '';
      return `${actieTekst}${maat}${beh}${item}${ref}`;
    }
    case 'stel_planningsgrens':
      return `Niet vóór ${s(p.vanaf)}: ${s(p.kant)}${ref}`;
    case 'stop_reiniging':
      return `Reiniging stoppen${s(p.item_referentie) ? ` ${s(p.item_referentie)}` : ''}${ref}: ${({ afvoeren: 'afvoeren', retour: 'onbehandeld retour', zelfhalen: 'zelf ophalen' } as Record<string, string>)[s(p.keuze)] ?? s(p.keuze)}`;
    case 'verwerk_risico_antwoord':
      return `Risico-antwoord verwerken${s(p.item_referentie) ? ` ${s(p.item_referentie)}` : ''}${ref}: ${({ akkoord: 'akkoord, toch reinigen', lichtere_behandeling: 'lichtere behandeling', afvoeren: 'afvoeren', retour: 'onbehandeld retour', zelfhalen: 'zelf ophalen' } as Record<string, string>)[s(p.keuze)] ?? s(p.keuze)}`;
    case 'corrigeer_factuur':
      return `Factuur corrigeren${ref}: ${typeof p.bedrag_incl_btw === 'number' ? `€ ${p.bedrag_incl_btw.toFixed(2)}` : `regel "${s(p.regel)}"`}${p.terugbetalen === true ? ' + terugbetalen' : ''}`;
    case 'maak_herhaalofferte':
      return `Herhaalofferte vanuit ${s(p.order_referentie)}: hetzelfde kleed, actuele prijzen`;
    case 'maak_vervolgopdracht':
      return `Vervolgopdracht vanuit ${s(p.order_referentie)} + orderbevestiging`;
    case 'bereid_partnerdoorverwijzing':
      return `Partneraanvraag voorbereiden: ${s(p.segment)}, ${s(p.postcode)}`;
    case 'plan_productnalevering':
      return `Kosteloze productnalevering vanuit ${s(p.order_referentie)}`;
    case 'create_offerte': {
      const producten = Array.isArray(p.producten) ? p.producten.length : 0;
      return `Offerte voor ${s(p.naam) || 'aanvrager'}: ${producten} ${producten === 1 ? 'kleed' : 'kleden'}${s(p.postcode) ? `, ${s(p.postcode)}` : ''}`;
    }
    case 'stuur_mail':
      return `${s(p.soort) === 'factuur' ? 'Factuur' : 'Bevestiging'} opnieuw sturen${ref}`;
    case 'annuleer_afspraak':
      return `${s(p.fase) === 'terugbrengen' ? 'Terugbreng' : 'Ophaal'}afspraak${ref} laten vervallen`;
    case 'voeg_email_toe':
      return `E-mailadres ${s(p.email)} toevoegen`;
    case 'plan_afspraak':
      return `Afspraak plannen${ref}${s(p.vanaf) ? ` vanaf ${s(p.vanaf)}` : ''}`;
    default:
      return actie.verb;
  }
}

/** Defensief lezen uit een API-/DB-veld dat jsonb of null kan zijn. */
export function leesActies(raw: unknown): VoorgesteldeActie[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is VoorgesteldeActie =>
      !!a && typeof a === 'object' && typeof (a as VoorgesteldeActie).verb === 'string',
  );
}

/** Badge-tekst per modus (voorbereiden = klik-modus voor mail). */
export function modusBadge(modus: MailVerbModi[string] | undefined): { tekst: string; title: string } {
  switch (modus) {
    case 'voorbereiden':
      return { tekst: 'wacht op akkoord', title: 'De agent voert dit pas uit na jouw Akkoord. Afwijzen met reden is leerdata.' };
    case 'live':
      return { tekst: 'wordt uitgevoerd bij verzenden', title: 'De agent voert dit zelf uit zodra je de reply verstuurt.' };
    case 'schaduw':
      return { tekst: 'schaduw: kantoor voert uit', title: 'De agent voert dit nog niet uit; kantoor doet het zelf. Wat jij doet wordt vergeleken met dit voorstel.' };
    default:
      return { tekst: 'uit', title: 'Dit verb staat voor mail uit.' };
  }
}
